<template>
  <div class="streak-widget">
    <div class="header-row">
      <span class="icon-wrapper">
        <icon name="fire" />
      </span>
      <span>Streak</span>
      <span class="spacer"></span>
      <span>
        <button
          class="clear-button"
          @click="showModal = true"
        >
          <icon name="info-circle" variant="98A2B3" />
        </button>
      </span>
    </div>
    <div class="hero">
      {{ currentStreakText }}
    </div>
    <modal-base
      :is-open="showModal"
      @close="showModal = false"
    >
      <p v-html="popoverHtml"></p>
    </modal-base>
  </div>
</template>

<script>
import Icon from '@components/singletons/icon';
import { differenceInDays } from 'date-fns';
import ModalBase from '@components/modals/ModalBase';

export default {
  name: 'StreakWidget',
  components: {
    Icon,
    ModalBase,
  },
  props: {
    flashcardStats: {
      type: Array,
      required: true,
    },
    streakInfoBody: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showModal: false,
    };
  },
  computed: {
    answeredQuestionToday() {
      return this.filteredStatsWithDifference[0]?.differenceInDays === 0;
    },
    currentStreak() {
      return this.getStreak(this.filteredStatsWithDifference);
    },
    currentStreakText() {
      return `${this.currentStreak} Day${this.currentStreak !== 1 ? 's' : ''}`;
    },
    daysSkipped() {
      return this.getDaysSkippedSoFar(this.filteredStatsWithDifference);
    },
    filteredStatsWithDifference() {
      const statsWithDifference = this.addDifferenceToStats(this.flashcardStats);

      if(this.streakStartDate) {
        return this.filterStatsByDate(statsWithDifference, this.streakStartDate);
      }
      return statsWithDifference;
    },
    firstDayOfStreak() {
      const stats = this.addDifferenceToStats(this.flashcardStats);
      const streakLength = this.getStreak(stats);
      const firstDay = stats.find((stat) => stat.differenceInDays === streakLength - 1);
      return firstDay;
    },
    popoverHtml() {
      return this.streakInfoBody || '<span>The amount of consecutive days in a row you\'ve answered at least one question.</span>';
    },
    streakStartDate() {
      return this.$store.getters['streak/getStartDate'];
    },
  },
  watch: {
    daysSkipped() {
      this.setNewStartDate();
    },
    answeredQuestionToday() {
      this.setNewStartDate();
    },
  },
  mounted() {
    this.setNewStartDate();
  },
  methods: {
    addDifferenceToStats(stats) {
      const today = new Date();
      return stats.map((stat) => ({
        ...stat,
        differenceInDays: differenceInDays(today, new Date(stat.date)),
      }));
    },
    filterStatsByDate(stats, maxDate) {
      if (maxDate) {
        return stats.filter((stat) => stat.date >= maxDate);
      }
      return stats;
    },
    getDaysSkippedSoFar(stats) {
      if (stats.length === 0) {
        return 0;
      }
      let daysSkipped = 0;

      for (let i = 0; i < stats.length; i++) {
        const currentDiff = stats[i].differenceInDays;
        const prevDiff = i > 0 ? stats[i - 1].differenceInDays : 0;
        const gap = currentDiff - prevDiff;

        if (gap > 1) {
          daysSkipped += gap - 1;
        }
      }
      return daysSkipped;
    },
    getStreak(filteredStats) {
      const MAX_DAYS_SKIPPED = 2;
      if (filteredStats.length === 0) {
        return 0;
      }

      let streak = 0;
      let daysSkipped = 0;
      for (let i = 0; i < filteredStats.length; i++) {
        const currentDiff = filteredStats[i].differenceInDays;
        const prevDiff = i > 0 ? filteredStats[i - 1].differenceInDays : 0;
        const gap = currentDiff - prevDiff;

        if (gap <= 1) {
          streak++;
        } else if (gap <= MAX_DAYS_SKIPPED + 1 && daysSkipped + gap - 1 <= MAX_DAYS_SKIPPED) {
          streak += gap;
          daysSkipped += gap - 1;
        } else {
          break;
        }
      }
      return streak;
    },
    setNewStartDate() {
      if (this.answeredQuestionToday && !this.streakStartDate) {
        this.$store.dispatch('streak/updateStreakStartDate', this.firstDayOfStreak?.date);
      } else if (this.answeredQuestionToday && this.daysSkipped >= 3) {
        this.$store.dispatch('streak/updateStreakStartDate', this.filteredStatsWithDifference[0].date);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.clear-button {
  background-color: transparent;
  padding: 0;
  margin: 0;
}
</style>
